/**
 * The API client.
 *
 * Same origin as the page, so there is no base URL to configure and no CORS.
 * Credentials ride on the session cookie, so the browser holds no bearer
 * token and there is nothing here to store or clear.
 *
 * Every path the web app calls on Stageville's server. The server answers
 * these under `/api`; stagepost's own `/v1` is only ever called by the server.
 * A house is named by Stageville's id for it, and its room is reached upstream
 * from there.
 *
 *   GET    /api/sign-in                                     (how anybody may sign in, asked signed out)
 *   GET    /api/me
 *   PUT    /api/me/agents                                   (whether Stageville may start agents)
 *   GET    /api/auth/{github,google}?next=<path>            (a link, not a fetch: starts sign-in there)
 *   POST   /api/accounts                                    (moving in)
 *   POST   /api/sessions                                    (coming home)
 *   POST   /api/password-resets
 *   POST   /api/password-resets/complete
 *   POST   /api/auth/sign-out
 *   POST   /api/login-codes
 *   GET    /api/neighbourhoods
 *   GET    /api/neighbourhoods/{neighbourhood}/houses        (the street, which is the room list)
 *   POST   /api/neighbourhoods/{neighbourhood}/houses        (a house for each repository picked)
 *   GET    /api/neighbourhoods/{neighbourhood}/repositories
 *   GET    /api/neighbourhoods/{neighbourhood}/github/connect?next=<path>  (a link, not a fetch: the owner brings repositories)
 *   GET    /api/neighbourhoods/{neighbourhood}/residents
 *   GET    /api/neighbourhoods/{neighbourhood}/direct?agent=&limit=   (private messages with one of your residents)
 *   POST   /api/neighbourhoods/{neighbourhood}/direct
 *   PUT    /api/neighbourhoods/{neighbourhood}/direct/read
 *   GET    /api/houses/{house}/messages?since=&limit=&threaded=
 *   POST   /api/houses/{house}/messages
 *   GET    /api/houses/{house}/threads/{root}
 *   PUT    /api/houses/{house}/messages/{message}/reactions/{reaction}
 *   DELETE /api/houses/{house}/messages/{message}/reactions/{reaction}
 *   GET    /api/houses/{house}/roster
 *   GET    /api/houses/{house}/presence
 *   GET    /api/houses/{house}/claims
 *   GET    /api/houses/{house}/status
 *   GET    /api/houses/{house}/machines
 *   GET    /api/houses/{house}/jobs
 *   POST   /api/houses/{house}/jobs
 *   GET    /api/houses/{house}/live?since=                    (SSE, in live/useLiveMessages.ts)
 *   GET    /api/houses/{house}
 *   PUT    /api/houses/{house}
 *   POST   /api/houses/{house}/cheats
 *   POST   /api/houses/{house}/undo
 *   GET    /api/house/catalogue
 *   GET    /api/assets/residents/residents.glb               (resident files, in stageville/residents/figure.ts)
 *   GET    /api/assets/residents/catalogue.json
 */
import type {
  AccountProblem,
  Agent,
  CatalogueEntry,
  Cheat,
  Claim,
  Credentials,
  DirectMessage,
  DirectPage,
  House,
  HouseShortfall,
  Job,
  JobRequest,
  LayoutRecord,
  LoginCode,
  Machines,
  Me,
  Message,
  MessagePage,
  Neighbourhood,
  NeighbourhoodHouse,
  PresenceSpan,
  ReactionName,
  Repository,
  Resident,
  RoomStatus,
  SignInOptions,
} from './types'

/** A response the server understood and refused. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** A refusal's `detail` when it carried more than a sentence or listed fields, an account call's `{problem}` body, else null. */
    readonly detail: unknown = null,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Not signed in: the signal to show the sign-in screen rather than log. */
  get isUnauthenticated(): boolean {
    return this.status === 401
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      // Only a string body is JSON. FormData has to set its own header so the
      // multipart boundary is right; naming the type here sends a body the
      // server cannot parse.
      ...(typeof init.body === 'string' ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  })

  if (!response.ok) {
    let detail = `request failed (${response.status})`
    let structured: unknown = null
    try {
      const body = await response.json()
      if (typeof body?.detail === 'string') detail = body.detail
      // An account call's refusal names its problem at the top level: `{"problem": "taken"}`.
      else if (typeof body?.problem === 'string') {
        detail = body.problem
        structured = body
      } else if (typeof body?.detail?.message === 'string') {
        detail = body.detail.message
        structured = body.detail
      }
      // FastAPI's own 422 lists each field it refused before the route ran.
      else if (Array.isArray(body?.detail)) structured = body.detail
    } catch {
      // A non-JSON error body is not worth a second failure.
    }
    throw new ApiError(detail, response.status, structured)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

const house = (houseId: string) => `/api/houses/${encodeURIComponent(houseId)}`
const neighbourhood = (neighbourhoodId: string) => `/api/neighbourhoods/${encodeURIComponent(neighbourhoodId)}`

export const api = {
  // --- who you are ---------------------------------------------------------
  signIn: () => request<SignInOptions>('/api/sign-in'),
  me: () => request<Me>('/api/me'),
  /** Make the account and sign it in. Refused with an `accountProblem`: refused, taken or weak. */
  moveIn: (credentials: Credentials) =>
    request<Me>('/api/accounts', { method: 'POST', body: JSON.stringify(credentials) }),
  /** Sign in to an account that exists. Refused with an `accountProblem`: wrong or refused. */
  comeHome: (credentials: Credentials) =>
    request<Me>('/api/sessions', { method: 'POST', body: JSON.stringify(credentials) }),
  /** Mail a reset link if an account has this email. Answers the same either way, so it never says which. */
  forgotPassword: (email: string) =>
    request<void>('/api/password-resets', { method: 'POST', body: JSON.stringify({ email }) }),
  /** Set a new password with the mailed link's token, and sign in. Refused with an `accountProblem`: spent or weak. */
  resetPassword: (token: string, password: string) =>
    request<Me>('/api/password-resets/complete', { method: 'POST', body: JSON.stringify({ token, password }) }),
  signOut: () => request<void>('/api/auth/sign-out', { method: 'POST' }),
  /** Let Stageville start agents for the account, or take that back. */
  allowAgents: (allowed: boolean) =>
    request<Me>('/api/me/agents', { method: 'PUT', body: JSON.stringify({ allowed }) }),
  /** A fresh code for `stagepost login --code`, which runs out after a few minutes. */
  loginCode: () => request<LoginCode>('/api/login-codes', { method: 'POST' }),

  // --- neighbourhoods and their houses -------------------------------------
  neighbourhoods: () => request<Neighbourhood[]>('/api/neighbourhoods'),
  /** Every room's house in one request, in repository order: the street, and the room list. */
  houses: (neighbourhoodId: string) => request<NeighbourhoodHouse[]>(`${neighbourhood(neighbourhoodId)}/houses`),
  /** Everything the neighbourhood's GitHub installation covers, built or not. */
  repositories: (neighbourhoodId: string) => request<Repository[]>(`${neighbourhood(neighbourhoodId)}/repositories`),
  /** Make a room and a house for each repository, and answer the houses made. */
  buildHouses: (neighbourhoodId: string, repositoryKeys: string[]) =>
    request<NeighbourhoodHouse[]>(`${neighbourhood(neighbourhoodId)}/houses`, {
      method: 'POST',
      body: JSON.stringify({ repository_keys: repositoryKeys }),
    }),

  // --- a house's room ------------------------------------------------------
  messages: (houseId: string, options: { since?: string; limit?: number; threaded?: boolean } = {}) => {
    const query = new URLSearchParams()
    if (options.since) query.set('since', options.since)
    if (options.limit) query.set('limit', String(options.limit))
    if (options.threaded) query.set('threaded', 'true')
    const suffix = query.toString() ? `?${query}` : ''
    return request<MessagePage>(`${house(houseId)}/messages${suffix}`)
  },
  post: (houseId: string, payload: { body: string; reply_to?: string | null }) =>
    request<Message>(`${house(houseId)}/messages`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  /** A message with every reply to it. */
  thread: (houseId: string, rootId: string) =>
    request<Message>(`${house(houseId)}/threads/${encodeURIComponent(rootId)}`),
  /** Put your reaction on a message, or take it off, and get the message back as it stands. */
  react: (houseId: string, messageId: string, reaction: ReactionName, on: boolean) =>
    request<Message>(`${house(houseId)}/messages/${encodeURIComponent(messageId)}/reactions/${reaction}`, {
      method: on ? 'PUT' : 'DELETE',
    }),

  /** The newest private messages with one of your residents. 404 when the agent is not yours, 503 when private messages are out of reach. */
  directMessages: (neighbourhoodId: string, agent: string, limit = 200) =>
    request<DirectPage>(`${neighbourhood(neighbourhoodId)}/direct?agent=${encodeURIComponent(agent)}&limit=${limit}`),
  sendDirect: (neighbourhoodId: string, payload: { agent: string; body: string; reply_to?: string | null }) =>
    request<DirectMessage>(`${neighbourhood(neighbourhoodId)}/direct`, { method: 'POST', body: JSON.stringify(payload) }),
  /** Move your read cursor with this resident up to the message `through`. */
  markDirectRead: (neighbourhoodId: string, agent: string, through: string) =>
    request<void>(`${neighbourhood(neighbourhoodId)}/direct/read`, { method: 'PUT', body: JSON.stringify({ agent, through }) }),
  agents: (houseId: string) => request<Agent[]>(`${house(houseId)}/roster`),
  /** Every session the room has kept, oldest first, stopped or running. */
  presence: (houseId: string) => request<PresenceSpan[]>(`${house(houseId)}/presence`),
  claims: (houseId: string) => request<Claim[]>(`${house(houseId)}/claims`),
  status: (houseId: string) => request<RoomStatus>(`${house(houseId)}/status`),

  // --- jobs ----------------------------------------------------------------
  /** Where the account can run a job for this house. */
  machines: (houseId: string) => request<Machines>(`${house(houseId)}/machines`),
  /** Start a job. 403 `{problem: "agents"}` until agents are allowed (`agentsRefused`), 409 `{problem: "no-machine", reason}` when no machine is online (`noMachine`). */
  startJob: (houseId: string, job: JobRequest) =>
    request<Job>(`${house(houseId)}/jobs`, { method: 'POST', body: JSON.stringify(job) }),
  /** The house's jobs, newest first. */
  jobs: (houseId: string) => request<Job[]>(`${house(houseId)}/jobs`),

  // --- residents -----------------------------------------------------------
  /**
   * Every face slot with a history or a look: its name, its current look, what
   * it has done and who it gets on with, so the house loads everyone at once.
   */
  residents: (neighbourhoodId: string) => request<Resident[]>(`${neighbourhood(neighbourhoodId)}/residents`),

  // --- houses --------------------------------------------------------------
  /** Everything a house can buy, with its price in credits. */
  houseCatalogue: () => request<{ entries: CatalogueEntry[] }>('/api/house/catalogue'),
  house: (houseId: string) => request<House>(house(houseId)),
  /**
   * Save the next version of the house, edited from `baseVersion`, and pay for
   * it. 409 when somebody saved since, 422 when the layout is refused, and 402
   * when the house cannot pay: read that one with `houseShortfall`. The first
   * save over the default house costs nothing, so build mode saves the default
   * it generated before its first edit, and that edit is priced against it.
   */
  saveHouse: (houseId: string, baseVersion: number, layout: LayoutRecord) =>
    request<House>(house(houseId), {
      method: 'PUT',
      body: JSON.stringify({ base_version: baseVersion, layout }),
    }),
  /** Enter a cheat in the house's ledger. 403 for an account the Stageville layout is not offered to. */
  cheat: (houseId: string, cheat: Cheat) =>
    request<House>(`${house(houseId)}/cheats`, { method: 'POST', body: JSON.stringify(cheat) }),
  /** Put back the version before `baseVersion`, which must be current, and every credit it moved. */
  undoHouse: (houseId: string, baseVersion: number) =>
    request<House>(`${house(houseId)}/undo`, {
      method: 'POST',
      body: JSON.stringify({ base_version: baseVersion }),
    }),
}

/** What a failed `saveHouse` lacked, or null when it failed for another reason. */
export function houseShortfall(error: unknown): HouseShortfall | null {
  return error instanceof ApiError && error.status === 402 ? (error.detail as HouseShortfall) : null
}

/** Whether a failed `startJob` was refused because the account has not allowed agents. */
export function agentsRefused(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403 && (error.detail as { problem?: unknown } | null)?.problem === 'agents'
}

/** Why a failed `startJob` found no machine to run on, in the upstream's words, or null when it failed for another reason. */
export function noMachine(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null
  const { problem, reason } = (error.detail ?? {}) as { problem?: unknown; reason?: unknown }
  return problem === 'no-machine' && typeof reason === 'string' ? reason : null
}

const PROBLEMS: ReadonlySet<string> = new Set<AccountProblem>(['refused', 'taken', 'wrong', 'weak', 'spent', 'failed'])

/** Why an account call refused somebody, or null when it failed for another reason. */
export function accountProblem(error: unknown): AccountProblem | null {
  if (!(error instanceof ApiError)) return null
  if (Array.isArray(error.detail)) {
    const fields: { loc?: unknown }[] = error.detail
    return fields.some(({ loc }) => Array.isArray(loc) && loc.includes('email')) ? 'email' : null
  }
  const problem = (error.detail as { problem?: unknown } | null)?.problem
  return typeof problem === 'string' && PROBLEMS.has(problem) ? (problem as AccountProblem) : null
}
